import { useEffect } from 'react';

import { useNavigate } from 'react-router-dom';

import { useAuth } from '../providers/AppAuthProvider';
import { useAppDispatch, useTypedSelector } from '../store';
import {
	clearError,
	completeOnboarding,
	completeStep,
	goToNextStep,
	goToPreviousStep,
	goToStep,
	resetOnboarding,
	setError,
	skipStep,
	startOnboarding,
	updateUploadedFileId,
} from '../store/onboardingSlice';
import {
	getOnboardingData,
	removeOnboardingData,
	saveOnboardingData,
} from '../utils/onboardingUtils';

export const useOnboarding = () => {
	const dispatch = useAppDispatch();
	const navigate = useNavigate();
	const { user } = useAuth();

	const { currentStepId, steps, isComplete, uploadedFileId, startedAt, error } =
		useTypedSelector((state) => state.onboarding);

	// Create onboardingState object to match old interface
	const onboardingState = {
		currentStep: currentStepId,
		isOnboarding: !isComplete && startedAt !== undefined,
		uploadedFileId,
		steps: steps,
		isComplete,
	};

	// Auto-save onboarding state to localStorage whenever it changes
	useEffect(() => {
		if (user?.email && (startedAt || uploadedFileId)) {
			const onboardingData = {
				completed: isComplete,
				currentStep: currentStepId,
				stepsCompleted: steps
					.filter((step) => step.completed)
					.map((step) => step.id),
				skippedSteps: [],
				uploadedFileId,
				startedAt,
			};

			saveOnboardingData(user.email, onboardingData);
		}
	}, [
		user?.email,
		currentStepId,
		steps,
		isComplete,
		uploadedFileId,
		startedAt,
	]);

	useEffect(() => {
		if (user?.email) {
			const path = window.location.pathname;
			if (path.startsWith('/onboarding')) {
				const onboardingData = getOnboardingData(user.email);
				if (onboardingData && !onboardingData.completed) {
					// Restore if we have stored data but missing current state
					if (!startedAt) {
						dispatch(
							startOnboarding({
								userEmail: user.email,
								fileId: onboardingData.uploadedFileId,
							}),
						);
					} else if (
						onboardingData.uploadedFileId &&
						onboardingData.uploadedFileId !== uploadedFileId
					) {
						dispatch(updateUploadedFileId(onboardingData.uploadedFileId));
					}
				} else if (!startedAt) {
					// Start fresh onboarding if no stored data
					dispatch(startOnboarding({ userEmail: user.email }));
				}
			}
		}
	}, [user?.email, startedAt, uploadedFileId, dispatch]);

	const startOnboardingFlow = (fileId?: string) => {
		dispatch(startOnboarding({ userEmail: user?.email, fileId }));
		// Navigate to the first step (upload page)
		navigate('/onboarding/upload');
	};

	const startOnboardingSimple = () => {
		dispatch(startOnboarding({ userEmail: user?.email }));
	};

	const completeCurrentStep = () => {
		dispatch(completeStep(currentStepId));
	};

	const completeStepById = (stepId: number) => {
		dispatch(completeStep(stepId));

		// If this is the current step and there's a next step, move to it (no navigation)
		if (stepId === currentStepId && currentStepId < steps.length) {
			dispatch(goToNextStep());
		}

		// If all steps are (now) completed, mark onboarding as complete and persist
		const allCompleted = steps.every((s) =>
			s.id === stepId ? true : s.completed,
		);
		if (allCompleted && !isComplete) {
			dispatch(completeOnboarding());
			if (user?.email) {
				const finalOnboardingData = {
					completed: true,
					currentStep: steps.length,
					stepsCompleted: steps.map((step) => step.id),
					skippedSteps: [],
					uploadedFileId,
					startedAt,
					completedAt: Date.now(),
				};
				saveOnboardingData(user.email, finalOnboardingData);
			}
		}
	};

	const goToNextStepAndNavigate = () => {
		dispatch(goToNextStep());
		const nextStep = steps.find((step) => step.id === currentStepId + 1);
		if (nextStep) {
			navigate(nextStep.path);
		}
	};

	const goToPreviousStepAndNavigate = () => {
		dispatch(goToPreviousStep());
		const prevStep = steps.find((step) => step.id === currentStepId - 1);
		if (prevStep) {
			navigate(prevStep.path);
		}
	};

	const goToStepAndNavigate = (stepId: number) => {
		dispatch(goToStep(stepId));
		const targetStep = steps.find((step) => step.id === stepId);
		if (targetStep) {
			navigate(targetStep.path);
		}
	};

	const skipCurrentStep = () => {
		dispatch(skipStep(currentStepId));
	};

	const updateFileId = (fileId: string) => {
		dispatch(updateUploadedFileId(fileId));
	};

	const finishOnboarding = () => {
		dispatch(completeOnboarding());
		// Save completion status to localStorage instead of removing it
		if (user?.email) {
			const finalOnboardingData = {
				completed: true,
				currentStep: steps.length,
				stepsCompleted: steps.map((step) => step.id),
				skippedSteps: [],
				uploadedFileId,
				startedAt,
				completedAt: Date.now(),
			};
			saveOnboardingData(user.email, finalOnboardingData);
		}
		navigate('/reports');
	};

	const exitOnboarding = () => {
		dispatch(resetOnboarding());
		if (user?.email) {
			removeOnboardingData(user.email);
		}
		navigate('/reports');
	};

	const setOnboardingError = (errorMessage: string) => {
		dispatch(setError(errorMessage));
	};

	const clearOnboardingError = () => {
		dispatch(clearError());
	};

	const nextStep = () => {
		dispatch(goToNextStep());
	};

	const previousStep = () => {
		dispatch(goToPreviousStep());
	};

	const isStepCompleted = (stepId: number) => {
		const step = steps.find((s) => s.id === stepId);
		return step?.completed || false;
	};

	const checkAndStartOnboarding = () => {
		if (!startedAt && !isComplete) {
			dispatch(startOnboarding({ userEmail: user?.email }));
		}
	};

	const getCurrentStep = () => steps.find((step) => step.id === currentStepId);

	const getStepStatus = (stepId: number) => {
		const step = steps.find((s) => s.id === stepId);
		if (!step) return 'upcoming';
		if (step.completed) return 'completed';
		if (step.id === currentStepId) return 'current';
		return 'upcoming';
	};

	const getProgressPercentage = () => {
		const completedSteps = steps.filter((step) => step.completed).length;
		return Math.round((completedSteps / steps.length) * 100);
	};
	// Listen for custom events to complete steps from Layout component
	useEffect(() => {
		const handleCompleteStep = (event: CustomEvent) => {
			const { stepId } = event.detail;
			if (stepId && stepId <= steps.length) {
				completeStepById(stepId);
			}
		};

		window.addEventListener(
			'completeStep',
			handleCompleteStep as EventListener,
		);

		return () => {
			window.removeEventListener(
				'completeStep',
				handleCompleteStep as EventListener,
			);
		};
	}, [steps.length, completeStepById]);

	return {
		// State
		currentStepId,
		steps,
		isComplete,
		uploadedFileId,
		startedAt,
		error,

		// Actions
		startOnboardingFlow,
		completeCurrentStep,
		completeStepById,
		goToNextStep: goToNextStepAndNavigate,
		goToPreviousStep: goToPreviousStepAndNavigate,
		goToStep: goToStepAndNavigate,
		skipCurrentStep,
		updateUploadedFileId: updateFileId,
		completeOnboarding: finishOnboarding,
		resetOnboarding: exitOnboarding,
		setError: setOnboardingError,
		clearError: clearOnboardingError,

		// Computed values
		getCurrentStep,
		getStepStatus,
		getProgressPercentage,

		// Additional functions for old Onboarding.tsx interface
		onboardingState,
		startOnboarding: startOnboardingFlow,
		completeStep: completeCurrentStep,
		nextStep,
		previousStep,
		exitOnboarding,
		isStepCompleted,
		checkAndStartOnboarding,
	};
};
